export interface FontInfo {
    family: string;
    source: string;
    url: string;
}

const COMMENT_REGEX = /<!-- @wysiwyg-fonts:(.*?) -->/;

export function extractFontComment(html: string): { fonts: FontInfo[]; cleanHtml: string } {
    const match = html.match(COMMENT_REGEX);

    if (!match) {
        return { fonts: [], cleanHtml: html };
    }

    let fonts: FontInfo[] = [];
    try {
        fonts = JSON.parse(match[1]);
    } catch {
        fonts = [];
    }

    const cleanHtml = html.replace(COMMENT_REGEX, '').trim();

    return { fonts, cleanHtml };
}

export function buildFontComment(html: string, fontList: FontInfo[]): string {
    if (!fontList.length) {
        return html;
    }

    // Strip any existing font comment
    const stripped = html.replace(COMMENT_REGEX, '').trim();

    // Strip HTML quote entities so regex can capture multi-word font names
    const decoded = stripped
        .replace(/&quot;/g, '')
        .replace(/&#39;/g, '');

    // Find all font-family values used in the HTML
    const fontFamilyRegex = /font-family:\s*([^;"]+)/g;
    const usedFamilies = new Set<string>();
    let fontMatch: RegExpExecArray | null;

    while ((fontMatch = fontFamilyRegex.exec(decoded)) !== null) {
        usedFamilies.add(fontMatch[1].trim().replace(/['"]/g, ''));
    }

    if (!usedFamilies.size) {
        return stripped;
    }

    // Match used families against the known font list
    const matchedFonts = fontList.filter((font) => usedFamilies.has(font.family));

    if (!matchedFonts.length) {
        return stripped;
    }

    const comment = `<!-- @wysiwyg-fonts:${JSON.stringify(matchedFonts)} -->`;

    return comment + stripped;
}

export function injectFontStylesheets(fonts: FontInfo[]): void {
    for (const font of fonts) {
        if (!font.url) {
            continue;
        }

        // Deduplicate by checking existing links
        if (document.querySelector(`link[data-wysiwyg-font="${CSS.escape(font.family)}"]`)) {
            continue;
        }

        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = font.url;
        link.dataset.wysiwygFont = font.family;
        document.head.appendChild(link);
    }
}
